import { c as _c } from "react/compiler-runtime";
import { AiSourceType, ProductImageMedium, ProductMedium, isAiGeneratedProductMedia, isProductVideoMedium } from '@spa/components/product-info/product-media/product-medium';
import CarouselComponent, { CarouselType } from '@spa/components/carousel/carousel.component';
import { CmsAiMarkerStyle, ProductImage } from '@spa/generated/graphql/graphql';
import { MouseEvent, ReactElement, Ref, memo, useMemo, useRef, useState } from 'react';
import { ProductMobileImageAsset, ProductTestRatingFrontViewAsset, ProductVideoAsset } from '@spa/components/ProductAssetTypes';
import { ImageAiMarkerComponent } from '@spa/components/image-ai-marker/image-ai-marker.component';
import ImageSectionComponent from '@spa/components/image-section/image-section.component';
import { NamespacedIntl } from '@spa/lib/i18n/common/NamespacedIntl';
import type { ProductInfoProductImage } from '@spa/components/product-info/ProductInfoProductTypes';
import ProductMediaExpanded from './product-media-expanded/product-media-expanded.component';
import VideoSectionComponent from '@spa/components/video-section/video-section.component';
import classNames from 'clsx';
import { createPortal } from 'react-dom';
import { fallbackProductImage } from '@spa/lib/const';
import { setToOriginal } from '../utils';
import styles from './product-media.component.module.scss';
import useNamespacedIntl from '@spa/lib/i18n/client/hooks/useNamespacedIntl';
import useValueOutsideFullscreen from '@spa/lib/react/hooks/useValueOutsideFullscreen';
import { useWidthSize } from '@spa/lib/react/hooks/useWidthSize';
export interface Props {
  readonly id: string;
  readonly images: readonly ProductInfoProductImage[] | null;
  readonly mobileImages: readonly ProductMobileImageAsset[];
  readonly priority: boolean;
  readonly productName: string;
  readonly productVideos: readonly ProductVideoAsset[];
  readonly cursorFollowingTooltip?: string;
  readonly testRatingFrontViewAsset?: ProductTestRatingFrontViewAsset | null | undefined;
}
function ProductImageElement(t0) {
  const $ = _c(34);
  const {
    url,
    aspectRatio,
    onMediaToggle,
    productName,
    priority,
    cursorFollowingTooltip,
    sizes,
    label,
    buttonRef,
    digitalSourceType
  } = t0;
  const {
    formatMessage: formatProductInfoMessage
  } = useNamespacedIntl("product-info");
  const {
    formatMessage: formatAiMarkerMessage
  } = useNamespacedIntl("image-ai-marker");
  const showAiMarker = !!digitalSourceType;
  let imageAlt;
  let t1;
  let withAiDisclosure;
  if ($[0] !== formatAiMarkerMessage || $[1] !== formatProductInfoMessage || $[2] !== productName || $[3] !== showAiMarker) {
    imageAlt = formatProductInfoMessage("imageAlt", {
      productName
    });
    let t2;
    if ($[7] !== formatAiMarkerMessage || $[8] !== showAiMarker) {
      t2 = text => showAiMarker ? `${text}, ${formatAiMarkerMessage("accessibleLabel")}` : text;
      $[7] = formatAiMarkerMessage;
      $[8] = showAiMarker;
      $[9] = t2;
    } else {
      t2 = $[9];
    }
    withAiDisclosure = t2;
    t1 = withAiDisclosure(imageAlt);
    $[0] = formatAiMarkerMessage;
    $[1] = formatProductInfoMessage;
    $[2] = productName;
    $[3] = showAiMarker;
    $[4] = imageAlt;
    $[5] = t1;
    $[6] = withAiDisclosure;
  } else {
    imageAlt = $[4];
    t1 = $[5];
    withAiDisclosure = $[6];
  }
  const displayAlt = t1;
  let t2;
  if ($[10] !== digitalSourceType) {
    t2 = digitalSourceType ? {
      "data-digital-source-type": digitalSourceType
    } : undefined;
    $[10] = digitalSourceType;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  const digitalSourceTypeAttr = t2;
  const t3 = label ?? imageAlt;
  let t4;
  if ($[12] !== t3 || $[13] !== withAiDisclosure) {
    t4 = withAiDisclosure(t3);
    $[12] = t3;
    $[13] = withAiDisclosure;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[15] !== url) {
    t5 = setToOriginal(url);
    $[15] = url;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== aspectRatio || $[18] !== cursorFollowingTooltip || $[19] !== digitalSourceTypeAttr || $[20] !== displayAlt || $[21] !== priority || $[22] !== sizes || $[23] !== t5) {
    t6 = <ImageSectionComponent alt={displayAlt} url={t5} priority={priority} cursorFollowingTooltip={cursorFollowingTooltip} sizes={sizes} aspectRatio={aspectRatio} digitalSourceTypeAttr={digitalSourceTypeAttr} />;
    $[17] = aspectRatio;
    $[18] = cursorFollowingTooltip;
    $[19] = digitalSourceTypeAttr;
    $[20] = displayAlt;
    $[21] = priority;
    $[22] = sizes;
    $[23] = t5;
    $[24] = t6;
  } else {
    t6 = $[24];
  }
  let t7;
  if ($[25] !== imageAlt || $[26] !== showAiMarker) {
    t7 = showAiMarker && <ImageAiMarkerComponent style={CmsAiMarkerStyle.BLACK_ON_WHITE} alt={imageAlt} />;
    $[25] = imageAlt;
    $[26] = showAiMarker;
    $[27] = t7;
  } else {
    t7 = $[27];
  }
  let t8;
  if ($[28] !== buttonRef || $[29] !== onMediaToggle || $[30] !== t4 || $[31] !== t6 || $[32] !== t7) {
    t8 = <button ref={buttonRef} type="button" className={styles["product-media__image-section"]} aria-label={t4} onClick={onMediaToggle}>{t6}{t7}</button>;
    $[28] = buttonRef;
    $[29] = onMediaToggle;
    $[30] = t4;
    $[31] = t6;
    $[32] = t7;
    $[33] = t8;
  } else {
    t8 = $[33];
  }
  return t8;
}
function MainMediaViewer(t0) {
  const $ = _c(12);
  const {
    item,
    videoActive,
    videoAutoplay,
    onMediaToggle,
    productName,
    priority,
    cursorFollowingTooltip,
    sizes,
    defaultAspectRatio,
    label,
    buttonRef
  } = t0;
  let t1;
  if ($[0] !== buttonRef || $[1] !== cursorFollowingTooltip || $[2] !== defaultAspectRatio || $[3] !== item || $[4] !== label || $[5] !== onMediaToggle || $[6] !== priority || $[7] !== productName || $[8] !== sizes || $[9] !== videoActive || $[10] !== videoAutoplay) {
    t1 = isProductVideoMedium(item) ? <button ref={buttonRef} type="button" className={styles["product-media__video-section"]} aria-label={label ?? productName} onClick={event => {
      if (!(event.target instanceof HTMLVideoElement)) {
        onMediaToggle();
      }
    }}><VideoSectionComponent active={videoActive} autoplay={videoAutoplay} aspectRatio={0.75} controls={true} url={item.url} /></button> : <ProductImageElement url={item.url} aspectRatio={defaultAspectRatio} onMediaToggle={onMediaToggle} productName={productName} priority={priority} cursorFollowingTooltip={cursorFollowingTooltip} sizes={sizes} label={label} buttonRef={buttonRef} digitalSourceType={isAiGeneratedProductMedia(item) ? item.digitalSourceType : undefined} />;
    $[0] = buttonRef;
    $[1] = cursorFollowingTooltip;
    $[2] = defaultAspectRatio;
    $[3] = item;
    $[4] = label;
    $[5] = onMediaToggle;
    $[6] = priority;
    $[7] = productName;
    $[8] = sizes;
    $[9] = videoActive;
    $[10] = videoAutoplay;
    $[11] = t1;
  } else {
    t1 = $[11];
  }
  return t1;
}
const MAX_ASPECT_RATIO: number = 0.75;
export const calculateMaxAspectRatio = (images?: ReadonlyArray<ProductImage>): number => {
  return images ? images.reduce((maxRatio, {
    width,
    height
  }) => {
    const aspectRatio: number = width / height;
    return Math.min(MAX_ASPECT_RATIO, Math.max(maxRatio, aspectRatio));
  }, 1) : MAX_ASPECT_RATIO;
};
function ProductMediaComponent(t0) {
  const $ = _c(56);
  const {
    id,
    images,
    mobileImages,
    priority,
    productName,
    productVideos,
    cursorFollowingTooltip,
    testRatingFrontViewAsset
  } = t0;
  const {
    formatMessage
  } = useNamespacedIntl("product-info");
  const [currentMainMedia, setCurrentMainMedia] = useState(0);
  const [settledMainMedia, setSettledMainMedia] = useState(0);
  const [openMedia, setOpenMedia] = useState(false);
  const currentMediaButtonRef = useRef(null);
  const {
    isMobile: isMobileViewport
  } = useWidthSize();
  const isMobile = useValueOutsideFullscreen(isMobileViewport);
  let t1;
  bb0: {
    if (testRatingFrontViewAsset) {
      const t2 = testRatingFrontViewAsset.dimensions?.width ?? 0;
      const t3 = testRatingFrontViewAsset.dimensions?.height ?? 0;
      let t4;
      if ($[0] !== t2 || $[1] !== t3 || $[2] !== testRatingFrontViewAsset.source) {
        t4 = {
          url: testRatingFrontViewAsset.source,
          width: t2,
          height: t3,
          type: "IMAGE"
        };
        $[0] = t2;
        $[1] = t3;
        $[2] = testRatingFrontViewAsset.source;
        $[3] = t4;
      } else {
        t4 = $[3];
      }
      t1 = t4;
      break bb0;
    }
    t1 = undefined;
  }
  const testRatingFrontViewImage = t1;
  let t2;
  if ($[4] !== mobileImages) {
    t2 = mobileImages?.map(_temp) ?? [];
    $[4] = mobileImages;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const mobileFrontViewImages = t2;
  let t3;
  if ($[6] !== productVideos) {
    t3 = productVideos?.map(_temp2) ?? [];
    $[6] = productVideos;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const videos = t3;
  let t4;
  if ($[8] !== images) {
    t4 = images?.map(_temp3) ?? [];
    $[8] = images;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const productImages = t4;
  let t5;
  if ($[10] !== testRatingFrontViewImage) {
    t5 = testRatingFrontViewImage ? [testRatingFrontViewImage] : [];
    $[10] = testRatingFrontViewImage;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const testRatingArray = t5;
  let t6;
  if ($[12] !== isMobile || $[13] !== mobileFrontViewImages) {
    t6 = isMobile ? mobileFrontViewImages : [];
    $[12] = isMobile;
    $[13] = mobileFrontViewImages;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const mobileImagesArray = t6;
  let t7;
  if ($[15] !== mobileImagesArray || $[16] !== productImages || $[17] !== testRatingArray || $[18] !== videos) {
    t7 = [...testRatingArray, ...mobileImagesArray, ...productImages, ...videos];
    $[15] = mobileImagesArray;
    $[16] = productImages;
    $[17] = testRatingArray;
    $[18] = videos;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  const media = t7;
  let t8;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = index => {
      setCurrentMainMedia(index);
      setSettledMainMedia(index);
    };
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const selectMedia = t8;
  let t9;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      setOpenMedia(_temp4);
    };
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  const handleMedia = t9;
  const t10 = !isMobile && !openMedia;
  let t11;
  if ($[22] !== cursorFollowingTooltip || $[23] !== productName || $[24] !== t10) {
    t11 = {
      onMediaToggle: handleMedia,
      productName,
      cursorFollowingTooltip,
      sizes: "\n    (max-width: 1023px) 100vw,\n    (max-width: 1440px) 50vw,\n    660px",
      videoAutoplay: t10
    };
    $[22] = cursorFollowingTooltip;
    $[23] = productName;
    $[24] = t10;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  const mediaViewerProps = t11 as const;
  let t12;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = calculateMaxAspectRatio();
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  const defaultAspectRatio = t12;
  let t13;
  if ($[27] !== currentMainMedia || $[28] !== id || $[29] !== media || $[30] !== openMedia || $[31] !== productName) {
    t13 = openMedia && createPortal(<ProductMediaExpanded id={`${id}-expanded`} open={openMedia} onClose={handleMedia} media={media} productName={productName} currentMainMedia={currentMainMedia} setCurrentMainMedia={selectMedia} returnFocusRef={currentMediaButtonRef} />, document.body);
    $[27] = currentMainMedia;
    $[28] = id;
    $[29] = media;
    $[30] = openMedia;
    $[31] = productName;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== currentMainMedia || $[34] !== formatMessage || $[35] !== media.length || $[36] !== openMedia || $[37] !== productName) {
    t14 = media.length > 1 && !openMedia && <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">{formatMessage("carouselImageAlt", {
        productName,
        index: currentMainMedia + 1,
        length: media.length
      })}</div>;
    $[33] = currentMainMedia;
    $[34] = formatMessage;
    $[35] = media.length;
    $[36] = openMedia;
    $[37] = productName;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  let t15;
  if ($[39] !== currentMainMedia || $[40] !== formatMessage || $[41] !== id || $[42] !== isMobile || $[43] !== media || $[44] !== mediaViewerProps || $[45] !== openMedia || $[46] !== priority || $[47] !== productName || $[48] !== settledMainMedia) {
    t15 = media?.length ? <>{isMobile && media.length > 1 ? <CarouselComponent id={`${id}-slides`} carouselType={CarouselType.BANNER} startIndex={currentMainMedia} onCurrentItemChange={setCurrentMainMedia} onCurrentItemSettled={setSettledMainMedia} snapToEveryItem={true}>{media.map((item, idx) => <MainMediaViewer key={item.url} item={item} videoActive={!openMedia && idx === currentMainMedia} priority={priority && idx === 0} defaultAspectRatio={defaultAspectRatio} label={formatMessage("carouselImageAlt", {
          productName,
          index: idx + 1,
          length: media.length
        })} buttonRef={idx === currentMainMedia ? currentMediaButtonRef : undefined} {...mediaViewerProps} />)}</CarouselComponent> : <MainMediaViewer item={media[currentMainMedia]} videoActive={!openMedia} priority={priority} defaultAspectRatio={defaultAspectRatio} buttonRef={currentMediaButtonRef} {...mediaViewerProps} />}{media.length > 1 && <CarouselComponent id={`${id}-carousel`} carouselType={CarouselType.MEDIA} title={formatMessage("carouselTitle", {
        productName
      })} startIndex={settledMainMedia}>{media.map((item_0, idx_0) => {
          const isSelected = idx_0 === currentMainMedia;
          return <button key={item_0.url} className={classNames(styles["product-media__tile"], {
            [styles["product-media__tile--selected"]]: isSelected
          })} onClick={() => selectMedia(idx_0)} onKeyDown={e => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              selectMedia(idx_0);
            }
          }} aria-label={formatMessage("carouselImageAlt", {
            productName,
            index: idx_0 + 1,
            length: media.length
          })} aria-pressed={isSelected}>{isProductVideoMedium(item_0) ? <VideoSectionComponent url={item_0.url} /> : <ImageSectionComponent alt="" url={setToOriginal(item_0.url)} priority={priority} sizes="(max-width: 768px) 50px, (max-width: 1024px) 62px, 74px" />}</button>;
        })}</CarouselComponent>}</> : <ProductImageElement url={fallbackProductImage.url} aspectRatio={MAX_ASPECT_RATIO} priority={priority} {...mediaViewerProps} />;
    $[39] = currentMainMedia;
    $[40] = formatMessage;
    $[41] = id;
    $[42] = isMobile;
    $[43] = media;
    $[44] = mediaViewerProps;
    $[45] = openMedia;
    $[46] = priority;
    $[47] = productName;
    $[48] = settledMainMedia;
    $[49] = t15;
  } else {
    t15 = $[49];
  }
  let t16;
  if ($[50] !== t14 || $[51] !== t15) {
    t16 = <div className={styles["product-media"]} data-component="ProductMedia">{t14}{t15}</div>;
    $[50] = t14;
    $[51] = t15;
    $[52] = t16;
  } else {
    t16 = $[52];
  }
  let t17;
  if ($[53] !== t13 || $[54] !== t16) {
    t17 = <>{t13}{t16}</>;
    $[53] = t13;
    $[54] = t16;
    $[55] = t17;
  } else {
    t17 = $[55];
  }
  return t17;
}
function _temp4(prev) {
  return !prev;
}
function _temp3(image) {
  return {
    ...image,
    digitalSourceType: image.digitalSourceType as AiSourceType,
    type: "IMAGE"
  };
}
function _temp2(video) {
  return {
    type: "VIDEO",
    url: video.source
  };
}
function _temp(img) {
  return {
    url: img.source,
    width: img.dimensions?.width ?? 0,
    height: img.dimensions?.height ?? 0,
    isMobile: true,
    type: "IMAGE"
  };
}
export default memo(ProductMediaComponent);